import React, { useState, useEffect } from 'react';
import axios from 'axios';

// CRITICAL: Configure axios to never cache requests
axios.defaults.headers.common['Cache-Control'] = 'no-cache, no-store, must-revalidate';
axios.defaults.headers.common['Pragma'] = 'no-cache';
axios.defaults.headers.common['Expires'] = '0';
import { 
  ChevronDown, ChevronUp, Phone, Mail, 
  MapPin, Clock, Package, User, ExternalLink, Truck, List, Grid, X, Maximize, Minimize, RefreshCw, CreditCard, QrCode, AlertCircle
} from 'lucide-react';

const CurrentOrders = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('ordersActiveTab') || 'pickup');
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [ecommerceSettings, setEcommerceSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState(() => localStorage.getItem('ordersTimeFilter') || 'today');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('ordersViewMode') || 'list');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCompleted, setShowCompleted] = useState(() => localStorage.getItem('ordersShowCompleted') === 'true');
  const [isFullscreen, setIsFullscreen] = useState(() => localStorage.getItem('ordersIsFullscreen') === 'true');
  const [currentPIN, setCurrentPIN] = useState(null);
  const [pinRegenerating, setPinRegenerating] = useState(false);
  const [showPaymentEnabledModal, setShowPaymentEnabledModal] = useState(false);
  const [enabledTableName, setEnabledTableName] = useState('');
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [barcodes, setBarcodes] = useState([]);
  const [loadingBarcodes, setLoadingBarcodes] = useState(false);
  const [showPaymentConfirmDialog, setShowPaymentConfirmDialog] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch orders
  const fetchOrders = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setIsRefreshing(true);
      console.log('🔄 Manual refresh triggered - fetching latest orders from database...');
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Always use cache-busting parameters to get fresh data
      const timestamp = new Date().getTime();
      const random = Math.random().toString(36).substring(7);
      
      const response = await axios.get(`${backendUrl}/api/orders?_t=${timestamp}&_r=${random}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      console.log(`✅ Fetched ${response.data.length} orders from database`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch ecommerce settings for workflow
  const fetchEcommerceSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/ecommerce-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = response.data;
      
      // Initialize default workflows if they don't exist
      const settingsWithWorkflows = {
        ...data,
        orderNowPickup: {
          ...(data.orderNowPickup || {}),
          workflow: data.orderNowPickup?.workflow || [
            { id: '1', label: 'Received', isFirst: true, isLast: false },
            { id: '2', label: 'Preparing', isFirst: false, isLast: false },
            { id: '3', label: 'Ready for Pickup', isFirst: false, isLast: false },
            { id: '4', label: 'Completed', isFirst: false, isLast: true }
          ]
        },
        orderNowDelivery: {
          ...(data.orderNowDelivery || {}),
          workflow: data.orderNowDelivery?.workflow || [
            { id: '1', label: 'Received', isFirst: true, isLast: false },
            { id: '2', label: 'Preparing', isFirst: false, isLast: false },
            { id: '3', label: 'Out for Delivery', isFirst: false, isLast: false },
            { id: '4', label: 'Delivered', isFirst: false, isLast: true }
          ]
        },
        shipping: {
          ...(data.shipping || {}),
          workflow: data.shipping?.workflow || [
            { id: '1', label: 'Received', isFirst: true, isLast: false },
            { id: '2', label: 'Processing', isFirst: false, isLast: false },
            { id: '3', label: 'In Transit', isFirst: false, isLast: false },
            { id: '4', label: 'Delivered', isFirst: false, isLast: true }
          ]
        },
        orderAtTable: {
          ...(data.orderAtTable || {}),
          workflow: data.orderAtTable?.workflow || [
            { id: '1', label: 'Received', isFirst: true, isLast: false },
            { id: '2', label: 'Preparing', isFirst: false, isLast: false },
            { id: '3', label: 'Ready', isFirst: false, isLast: false },
            { id: '4', label: 'Completed', isFirst: false, isLast: true }
          ]
        }
      };
      
      setEcommerceSettings(settingsWithWorkflows);
    } catch (error) {
      console.error('Error fetching ecommerce settings:', error);
    }
  };

  // Fetch current PIN for Order at Table
  const fetchCurrentPIN = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/order-at-table/current-pin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.pin && !response.data.expired) {
        setCurrentPIN(response.data);
      } else {
        setCurrentPIN(null);
      }
    } catch (error) {
      console.error('Error fetching PIN:', error);
      setCurrentPIN(null);
    }
  };

  // Regenerate PIN
  const regeneratePIN = async () => {
    setPinRegenerating(true);
    try {
      const token = localStorage.getItem('token');
      const pinType = currentPIN?.type || 'daily';
      const response = await axios.post(
        `${backendUrl}/api/order-at-table/generate-pin?pin_type=${pinType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data && response.data.pin) {
        setCurrentPIN({ 
          pin: response.data.pin, 
          type: pinType, 
          expires_at: response.data.expires_at, 
          expired: false 
        });
      }
    } catch (error) {
      console.error('Error regenerating PIN:', error);
    } finally {
      setPinRegenerating(false);
    }
  };

  // Enable Pay at Table for an order
  const handleEnablePayAtTable = async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Find the order to get table name
      const order = orders.find(o => o.id === orderId);
      const tableName = order?.table_name || order?.assigned_table_name || 'this table';
      
      const response = await axios.patch(
        `${backendUrl}/api/orders/${orderId}`,
        { paymentStatus: 'pending_payment' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setEnabledTableName(tableName);
        setShowPaymentEnabledModal(true);
        fetchOrders(); // Refresh orders
      }
    } catch (error) {
      console.error('Error enabling Pay at Table:', error);
      setErrorMessage('Failed to enable Pay at Table: ' + (error.response?.data?.detail || error.message));
      setShowErrorModal(true);
    }
  };

  // Fetch barcodes
  const fetchBarcodes = async () => {
    setLoadingBarcodes(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/pay-at-table/barcodes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Barcodes API response:', response.data);
      if (response.data.success) {
        console.log('Setting barcodes:', response.data.barcodes);
        setBarcodes(response.data.barcodes || []);
      } else {
        console.warn('Barcodes fetch failed:', response.data);
        setBarcodes([]);
      }
    } catch (error) {
      console.error('Error fetching barcodes:', error);
      console.error('Error details:', error.response?.data);
      setBarcodes([]);
    } finally {
      setLoadingBarcodes(false);
    }
  };

  // Assign barcode to order
  const handleAssignBarcode = async (barcodeId, orderId) => {
    try {
      const token = localStorage.getItem('token');
      const order = orders.find(o => o.id === orderId);
      
      if (!order) {
        console.error('Order not found:', orderId);
        setErrorMessage('Order not found');
        setShowErrorModal(true);
        return;
      }
      
      console.log('Assigning barcode:', {
        barcodeId,
        orderId,
        table_id: order.assigned_table_id || order.id,
        table_name: order.table_name || order.assigned_table_name
      });
      
      const response = await axios.patch(
        `${backendUrl}/api/pay-at-table/barcodes/${barcodeId}`,
        {
          assigned_table_id: order.assigned_table_id || order.id,
          assigned_table_name: order.table_name || order.assigned_table_name || `Order ${order.order_number}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Assignment response:', response.data);
      
      if (response.data.success) {
        console.log('✅ Barcode assigned successfully, refreshing list...');
        await fetchBarcodes(); // Refresh barcodes
      } else {
        console.error('Assignment failed:', response.data);
        setErrorMessage('Failed to assign barcode: ' + (response.data.message || 'Unknown error'));
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error assigning barcode:', error);
      console.error('Error response:', error.response?.data);
      setErrorMessage('Failed to assign barcode: ' + (error.response?.data?.detail || error.response?.data?.message || error.message));
      setShowErrorModal(true);
    }
  };

  // Unassign barcode
  const handleUnassignBarcode = async (barcodeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${backendUrl}/api/pay-at-table/barcodes/${barcodeId}`,
        {
          assigned_table_id: null,
          assigned_table_name: null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        fetchBarcodes(); // Refresh barcodes
      }
    } catch (error) {
      console.error('Error unassigning barcode:', error);
      setErrorMessage('Failed to unassign barcode: ' + (error.response?.data?.detail || error.message));
      setShowErrorModal(true);
    }
  };

  // Open barcode modal
  const openBarcodeModal = () => {
    setShowBarcodeModal(true);
    fetchBarcodes();
  };

  useEffect(() => {
    fetchOrders(false);
    fetchEcommerceSettings();
  }, []);

  // Fetch PIN when Dine In tab is active and PIN is enabled
  useEffect(() => {
    if (activeTab === 'inStore' && ecommerceSettings?.orderAtTable?.enablePIN) {
      fetchCurrentPIN();
      // Refresh PIN every 30 seconds
      const interval = setInterval(fetchCurrentPIN, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab, ecommerceSettings]);

  // Handle fullscreen mode - hide/show header
  useEffect(() => {
    const header = document.querySelector('header');
    if (isFullscreen) {
      if (header) header.style.display = 'none';
    } else {
      if (header) header.style.display = '';
    }
    
    // Cleanup on unmount
    return () => {
      if (header) header.style.display = '';
    };
  }, [isFullscreen]);

  // Save filter preferences to localStorage
  useEffect(() => {
    localStorage.setItem('ordersActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('ordersTimeFilter', timeFilter);
  }, [timeFilter]);

  useEffect(() => {
    localStorage.setItem('ordersViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('ordersShowCompleted', showCompleted.toString());
  }, [showCompleted]);

  useEffect(() => {
    localStorage.setItem('ordersIsFullscreen', isFullscreen.toString());
  }, [isFullscreen]);

  // Parse ISO datetime string without timezone conversion
  const parseBusinessDateTime = (isoString) => {
    if (!isoString) return null;
    
    const parts = isoString.split('T');
    if (parts.length !== 2) return null;
    
    const [datePart, timePart] = parts;
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    
    return {
      year,
      month,
      day,
      hours,
      minutes,
      date: datePart,
      time: timePart
    };
  };

  // Get today's date (business timezone)
  const getTodayDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  };

  // Get yesterday's date
  const getYesterdayDate = () => {
    const today = getTodayDate();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  };

  // Get tomorrow's date
  const getTomorrowDate = () => {
    const today = getTodayDate();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  };

  // Get the target date based on time filter
  const getTargetDate = () => {
    if (timeFilter === 'yesterday') return getYesterdayDate();
    if (timeFilter === 'tomorrow') return getTomorrowDate();
    return getTodayDate(); // default to today
  };

  // Check if a date is within the selected range
  const isDateInRange = (orderDate) => {
    if (!orderDate) return false;
    
    const parsed = parseBusinessDateTime(orderDate);
    if (!parsed) return false;
    
    const orderDateObj = new Date(parsed.year, parsed.month - 1, parsed.day);
    const today = getTodayDate();
    
    if (timeFilter === 'past7days') {
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return orderDateObj >= sevenDaysAgo && orderDateObj <= today;
    } else if (timeFilter === 'past30days') {
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return orderDateObj >= thirtyDaysAgo && orderDateObj <= today;
    } else if (timeFilter === 'past180days') {
      const oneEightyDaysAgo = new Date(today);
      oneEightyDaysAgo.setDate(oneEightyDaysAgo.getDate() - 180);
      return orderDateObj >= oneEightyDaysAgo && orderDateObj <= today;
    }
    
    return false;
  };

  // Format date for display badge (e.g., "7th Dec, 2024" or "Past 7 Days")
  const getFormattedDateDisplay = () => {
    // Show range label for date range filters
    if (timeFilter === 'past7days') return 'Past 7 Days';
    if (timeFilter === 'past30days') return 'Past 30 Days';
    if (timeFilter === 'past180days') return 'Past 180 Days';
    
    const targetDate = getTargetDate();
    const day = targetDate.getDate();
    const month = targetDate.toLocaleString('en-US', { month: 'short' });
    const year = targetDate.getFullYear();
    
    // Get ordinal suffix
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${getOrdinalSuffix(day)} ${month}, ${year}`;
  };

  // Get workflow for order type
  const getWorkflowForOrderType = (orderTypeLabel, orderType) => {
    if (!ecommerceSettings) return null;
    
    // Map for orderTypeLabel
    const orderTypeMap = {
      'Shipping': 'shipping',
      'Order Now - Pickup': 'orderNowPickup',
      'Order Now - Delivery': 'orderNowDelivery',
      'Pre-Order - Pickup': 'orderNowPickup',
      'Pre-Order - Delivery': 'orderNowDelivery',
      'Order at Table': 'orderAtTable',
      'Order at Counter': 'orderAtCounter'
    };
    
    // Fallback: if orderTypeLabel is null/undefined, try to use orderType directly
    let settingsKey = orderTypeMap[orderTypeLabel];
    if (!settingsKey && orderType) {
      // Direct mapping from orderType to settings key
      const directOrderTypeMap = {
        'orderNowPickup': 'orderNowPickup',
        'orderNowDelivery': 'orderNowDelivery',
        'orderAtTable': 'orderAtTable',
        'orderAtTablePIN': 'orderAtTable',
        'orderAtCounter': 'orderAtCounter',
        'preOrderPickup': 'preOrderPickup',
        'preOrderDelivery': 'preOrderDelivery',
        'shipping': 'shipping'
      };
      settingsKey = directOrderTypeMap[orderType];
    }
    
    if (!settingsKey) return null;
    
    const orderTypeSettings = ecommerceSettings[settingsKey];
    if (!orderTypeSettings) return null;
    
    // For Order at Table: If PIN is enabled, use workflowPIN
    let workflowKey = 'workflow';
    if (settingsKey === 'orderAtTable' && orderTypeSettings.enablePIN) {
      workflowKey = 'workflowPIN';
    }
    
    return orderTypeSettings[workflowKey];
  };

  // Get current step and next step for an order
  const getOrderSteps = (order) => {
    const workflow = getWorkflowForOrderType(order.orderTypeLabel, order.orderType);
    if (!workflow || workflow.length === 0) {
      return { currentStep: null, nextStep: null, currentIndex: -1 };
    }
    
    let statusToMatch = order.status.toLowerCase().trim();
    if (statusToMatch === 'pending' || statusToMatch === 'received') {
      return { 
        currentStep: workflow[0], 
        nextStep: workflow.length > 1 ? workflow[1] : null,
        currentIndex: 0
      };
    }
    
    const currentIndex = workflow.findIndex(step => 
      step.label.toLowerCase().trim() === statusToMatch
    );
    
    if (currentIndex === -1) {
      return { 
        currentStep: workflow[0], 
        nextStep: workflow.length > 1 ? workflow[1] : null,
        currentIndex: 0
      };
    }
    
    const currentStep = workflow[currentIndex];
    const nextStep = currentIndex < workflow.length - 1 ? workflow[currentIndex + 1] : null;
    
    return { currentStep, nextStep, currentIndex };
  };

  // Get count for each tab (respects time filter and pending/completed status)
  const getTabCounts = () => {
    const targetDate = getTargetDate();
    
    const isCompleted = (order) => {
      // Fallback: Check if status is literally "Completed" for orders without proper workflow
      if (order.status && order.status.toLowerCase() === 'completed') {
        return true;
      }
      
      const { currentStep } = getOrderSteps(order);
      return currentStep?.isLast === true;
    };
    const isInTransit = (order) => order.orderTypeLabel === 'Shipping' && order.status === 'in-transit';
    
    const pickupCount = orders.filter(order => {
      const isPickupOrder = order.orderTypeLabel?.includes('Pickup');
      if (!isPickupOrder) return false;
      
      // Filter by completed status
      if (showCompleted) {
        if (!isCompleted(order)) return false;
      } else {
        if (isCompleted(order)) return false;
      }
      
      // Filter by date or date range
      const isRangeFilter = ['past7days', 'past30days', 'past180days'].includes(timeFilter);
      
      if (isRangeFilter) {
        // Check if order is within the date range
        if (order.pickupDate) {
          return isDateInRange(order.pickupDate);
        }
        if (!order.pickupDate && order.orderTypeLabel === 'Order Now - Pickup') {
          return isDateInRange(order.orderDate);
        }
        return false;
      } else {
        // Check for exact date match
        if (order.pickupDate) {
          const parsed = parseBusinessDateTime(order.pickupDate);
          if (parsed) {
            const pickupDate = new Date(parsed.year, parsed.month - 1, parsed.day);
            return pickupDate.getTime() === targetDate.getTime();
          }
        }
        
        if (!order.pickupDate && order.orderTypeLabel === 'Order Now - Pickup') {
          const orderParsed = parseBusinessDateTime(order.orderDate);
          if (orderParsed) {
            const orderDay = new Date(orderParsed.year, orderParsed.month - 1, orderParsed.day);
            return orderDay.getTime() === targetDate.getTime();
          }
        }
        return false;
      }
    }).length;

    const deliveryCount = orders.filter(order => {
      const isDeliveryOrder = order.orderTypeLabel?.includes('Delivery');
      if (!isDeliveryOrder) return false;
      
      // Filter by completed status
      if (showCompleted) {
        if (!isCompleted(order)) return false;
      } else {
        if (isCompleted(order)) return false;
      }
      
      // Filter by date or date range
      const isRangeFilter = ['past7days', 'past30days', 'past180days'].includes(timeFilter);
      
      if (isRangeFilter) {
        // Check if order is within the date range
        if (order.deliveryDate) {
          return isDateInRange(order.deliveryDate);
        }
        if (!order.deliveryDate && order.orderTypeLabel === 'Order Now - Delivery') {
          return isDateInRange(order.orderDate);
        }
        return false;
      } else {
        // Check for exact date match
        if (order.deliveryDate) {
          const parsed = parseBusinessDateTime(order.deliveryDate);
          if (parsed) {
            const deliveryDate = new Date(parsed.year, parsed.month - 1, parsed.day);
            return deliveryDate.getTime() === targetDate.getTime();
          }
        }
        
        if (!order.deliveryDate && order.orderTypeLabel === 'Order Now - Delivery') {
          const orderParsed = parseBusinessDateTime(order.orderDate);
          if (orderParsed) {
            const orderDay = new Date(orderParsed.year, orderParsed.month - 1, orderParsed.day);
            return orderDay.getTime() === targetDate.getTime();
          }
        }
        return false;
      }
    }).length;

    const shippingCount = orders.filter(order => {
      const isShippingOrder = order.orderTypeLabel === 'Shipping';
      if (!isShippingOrder) return false;
      
      // Filter by completed status
      if (showCompleted) {
        if (!isCompleted(order)) return false;
      } else {
        if (isCompleted(order) || isInTransit(order)) return false;
      }
      
      // Filter by date or date range
      const isRangeFilter = ['past7days', 'past30days', 'past180days'].includes(timeFilter);
      
      if (isRangeFilter) {
        return isDateInRange(order.orderDate);
      } else {
        const orderParsed = parseBusinessDateTime(order.orderDate);
        if (orderParsed) {
          const orderDay = new Date(orderParsed.year, orderParsed.month - 1, orderParsed.day);
          return orderDay.getTime() === targetDate.getTime();
        }
        return false;
      }
    }).length;

    const inStoreCount = orders.filter(order => {
      const isInStoreOrder = order.orderType === 'orderAtTable' || order.orderTypeLabel === 'Order at Table' || 
                             (order.orderType === 'orderAtCounter' && order.specialInstructions === 'Dine In Order');
      if (!isInStoreOrder) return false;
      
      // Filter by completed status
      if (showCompleted) {
        if (!isCompleted(order)) return false;
      } else {
        if (isCompleted(order)) return false;
      }
      
      // Filter by date or date range
      const isRangeFilter = ['past7days', 'past30days', 'past180days'].includes(timeFilter);
      
      if (isRangeFilter) {
        return isDateInRange(order.orderDate);
      } else {
        const orderParsed = parseBusinessDateTime(order.orderDate);
        if (orderParsed) {
          const orderDay = new Date(orderParsed.year, orderParsed.month - 1, orderParsed.day);
          return orderDay.getTime() === targetDate.getTime();
        }
        return false;
      }
    }).length;

    return { pickupCount, deliveryCount, shippingCount, inStoreCount };
  };

  const tabCounts = getTabCounts();

  // Calculate estimated delivery date for shipping orders
  const calculateEstimatedDelivery = (order) => {
    if (order.orderTypeLabel !== 'Shipping') return null;
    
    // Get order creation date
    const parsed = parseBusinessDateTime(order.orderDate);
    if (!parsed) return null;
    
    // Default delivery days if no shipping option
    let deliveryDays = 5;
    
    // Check shipping option for delivery time info
    if (order.shippingOption) {
      const description = order.shippingOption.description || '';
      // Try to extract days from description like "Arrives in 3-5 business days" or "2-3 days"
      const daysMatch = description.match(/(\d+)-(\d+)\s*(business\s*)?days?/i);
      if (daysMatch) {
        // Use the maximum days for safety
        deliveryDays = parseInt(daysMatch[2]);
      } else {
        // Check for single day mention
        const singleDayMatch = description.match(/(\d+)\s*(business\s*)?days?/i);
        if (singleDayMatch) {
          deliveryDays = parseInt(singleDayMatch[1]);
        }
      }
    }
    
    // Calculate delivery date (add days to order date)
    const orderDate = new Date(parsed.year, parsed.month - 1, parsed.day);
    const deliveryDate = new Date(orderDate);
    deliveryDate.setDate(orderDate.getDate() + deliveryDays);
    
    // Format delivery date
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    const deliveryDay = deliveryDate.getDate();
    const deliveryMonth = monthNames[deliveryDate.getMonth()];
    const deliveryDayName = dayNames[deliveryDate.getDay()];
    
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${deliveryMonth} ${deliveryDay}${getOrdinalSuffix(deliveryDay)} (${deliveryDayName})`;
  };

  // Filter orders based on active tab and time filter
  const getFilteredOrders = () => {
    const targetDate = getTargetDate();
    
    // Helper to check if order is completed (at last step of workflow)
    const isCompleted = (order) => {
      // Fallback: Check if status is literally "Completed" for orders without proper workflow
      if (order.status && order.status.toLowerCase() === 'completed') {
        if (!showCompleted) {
          console.log(`✓ Hiding completed order: ${order.order_number} (status: ${order.status}) [fallback check]`);
        }
        return true;
      }
      
      const { currentStep } = getOrderSteps(order);
      const completed = currentStep?.isLast === true;
      // Debug log for completed orders
      if (completed && !showCompleted) {
        console.log(`✓ Hiding completed order: ${order.order_number} (status: ${order.status})`);
      }
      return completed;
    };
    
    // Helper to check if shipping order is in transit
    const isInTransit = (order) => {
      return order.orderTypeLabel === 'Shipping' && order.status === 'in-transit';
    };
    
    if (activeTab === 'pickup') {
      return orders.filter(order => {
        const isPickupOrder = order.orderTypeLabel?.includes('Pickup');
        if (!isPickupOrder) return false;
        
        // Filter by completed status
        if (showCompleted) {
          if (!isCompleted(order)) return false;
        } else {
          if (isCompleted(order)) return false;
        }
        
        // Filter by date or date range
        const isRangeFilter = ['past7days', 'past30days', 'past180days'].includes(timeFilter);
        
        if (isRangeFilter) {
          // Check if order is within the date range
          if (order.pickupDate) {
            return isDateInRange(order.pickupDate);
          }
          if (!order.pickupDate && order.orderTypeLabel === 'Order Now - Pickup') {
            return isDateInRange(order.orderDate);
          }
          return false;
        } else {
          // Show orders matching the selected exact date
          if (order.pickupDate) {
            const parsed = parseBusinessDateTime(order.pickupDate);
            if (parsed) {
              const pickupDate = new Date(parsed.year, parsed.month - 1, parsed.day);
              return pickupDate.getTime() === targetDate.getTime();
            }
          }
          
          // For Order Now - Pickup (no future date), check if created on target date
          if (!order.pickupDate && order.orderTypeLabel === 'Order Now - Pickup') {
            const orderParsed = parseBusinessDateTime(order.orderDate);
            if (orderParsed) {
              const orderDay = new Date(orderParsed.year, orderParsed.month - 1, orderParsed.day);
              return orderDay.getTime() === targetDate.getTime();
            }
          }
          
          return false;
        }
      });
    } else if (activeTab === 'delivery') {
      return orders.filter(order => {
        const isDeliveryOrder = order.orderTypeLabel?.includes('Delivery');
        if (!isDeliveryOrder) return false;
        
        // Filter by completed status
        if (showCompleted) {
          if (!isCompleted(order)) return false;
        } else {
          if (isCompleted(order)) return false;
        }
        
        // Filter by date or date range
        const isRangeFilter = ['past7days', 'past30days', 'past180days'].includes(timeFilter);
        
        if (isRangeFilter) {
          // Check if order is within the date range
          if (order.deliveryDate) {
            return isDateInRange(order.deliveryDate);
          }
          if (!order.deliveryDate && order.orderTypeLabel === 'Order Now - Delivery') {
            return isDateInRange(order.orderDate);
          }
          return false;
        } else {
          // Show orders matching the selected exact date
          if (order.deliveryDate) {
            const parsed = parseBusinessDateTime(order.deliveryDate);
            if (parsed) {
              const deliveryDate = new Date(parsed.year, parsed.month - 1, parsed.day);
              return deliveryDate.getTime() === targetDate.getTime();
            }
          }
          
          // For Order Now - Delivery (no future date), check if created on target date
          if (!order.deliveryDate && order.orderTypeLabel === 'Order Now - Delivery') {
            const orderParsed = parseBusinessDateTime(order.orderDate);
            if (orderParsed) {
              const orderDay = new Date(orderParsed.year, orderParsed.month - 1, orderParsed.day);
              return orderDay.getTime() === targetDate.getTime();
            }
          }
          
          return false;
        }
      });
    } else if (activeTab === 'shipping') {
      return orders.filter(order => {
        const isShippingOrder = order.orderTypeLabel === 'Shipping';
        if (!isShippingOrder) return false;
        
        // Filter by completed status
        if (showCompleted) {
          if (!isCompleted(order)) return false;
        } else {
          if (isCompleted(order)) return false;
          // Exclude in-transit orders (already shipped)
          if (isInTransit(order)) return false;
        }
        
        // Filter by date or date range
        const isRangeFilter = ['past7days', 'past30days', 'past180days'].includes(timeFilter);
        
        if (isRangeFilter) {
          return isDateInRange(order.orderDate);
        } else {
          // Show shipping orders created on target date
          const orderParsed = parseBusinessDateTime(order.orderDate);
          if (orderParsed) {
            const orderDay = new Date(orderParsed.year, orderParsed.month - 1, orderParsed.day);
            return orderDay.getTime() === targetDate.getTime();
          }
          return false;
        }
      });
    } else if (activeTab === 'inStore') {
      return orders.filter(order => {
        const isInStoreOrder = order.orderType === 'orderAtTable' || order.orderTypeLabel === 'Order at Table' || 
                               (order.orderType === 'orderAtCounter' && (order.assigned_table_name || order.specialInstructions === 'Dine In Order'));
        if (!isInStoreOrder) return false;
        
        // Filter by completed status
        if (showCompleted) {
          if (!isCompleted(order)) return false;
        } else {
          if (isCompleted(order)) return false;
        }
        
        // Filter by date or date range
        const isRangeFilter = ['past7days', 'past30days', 'past180days'].includes(timeFilter);
        
        if (isRangeFilter) {
          return isDateInRange(order.orderDate);
        } else {
          // Show in-store orders created on target date
          const orderParsed = parseBusinessDateTime(order.orderDate);
          if (orderParsed) {
            const orderDay = new Date(orderParsed.year, orderParsed.month - 1, orderParsed.day);
            return orderDay.getTime() === targetDate.getTime();
          }
          return false;
        }
      });
    }
    
    return [];
  };

  const filteredOrders = getFilteredOrders();

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${backendUrl}/api/orders/${orderId}/status`,
        { status: newStatus },
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { status: newStatus }
        }
      );
      
      // Refresh orders
      await fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      setErrorMessage('Failed to update order status: ' + (error.response?.data?.detail || error.message));
      setShowErrorModal(true);
    }
  };

  // Move order to next workflow step
  const moveToNextStep = async (order) => {
    try {
      const { nextStep } = getOrderSteps(order);
      if (!nextStep) return;
      
      await updateOrderStatus(order.id, nextStep.label);
    } catch (error) {
      console.error('Error moving to next step:', error);
    }
  };

  // Format date/time
  const formatOrderHeaderDateTime = (dateStr) => {
    if (!dateStr) return '';
    
    const parsed = parseBusinessDateTime(dateStr);
    if (!parsed) return '';
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[parsed.month - 1];
    
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    const ordinalDay = `${parsed.day}${getOrdinalSuffix(parsed.day)}`;
    const hours12 = parsed.hours % 12 || 12;
    const ampm = parsed.hours >= 12 ? 'PM' : 'AM';
    const formattedMinutes = parsed.minutes < 10 ? `0${parsed.minutes}` : parsed.minutes;
    
    return `${month} ${ordinalDay}, ${hours12}:${formattedMinutes} ${ampm} EST`;
  };

  const getStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('received') || statusLower.includes('pending')) return 'bg-theme-blue';
    if (statusLower.includes('preparing') || statusLower.includes('processing')) return 'bg-yellow-500';
    if (statusLower.includes('ready') || statusLower.includes('transit')) return 'bg-purple-500';
    if (statusLower.includes('completed') || statusLower.includes('delivered')) return 'bg-green-500';
    if (statusLower.includes('cancelled')) return 'bg-red-500';
    return 'bg-gray-500';
  };

  const handleStatusChange = async (orderId, newStatus, skipConfirmation = false) => {
    console.log('handleStatusChange called:', { orderId, newStatus, skipConfirmation });
    
    // Find the order
    const order = orders.find(o => o.id === orderId);
    if (!order) {
      console.error('Order not found:', orderId);
      return;
    }
    
    // Get current and next steps
    const { currentStep } = getOrderSteps(order);
    console.log('Current step:', currentStep);
    
    // Check if current step is a payment step and user is manually completing
    const isManualCompleteFromPaymentStep = currentStep?.isPaymentStep && newStatus.toLowerCase() === 'completed';
    console.log('Is manual complete from payment step:', isManualCompleteFromPaymentStep);
    
    // Show confirmation dialog if manually completing from a payment step
    if (isManualCompleteFromPaymentStep && !skipConfirmation) {
      console.log('Showing confirmation dialog');
      setPendingStatusChange({ orderId, newStatus });
      setShowPaymentConfirmDialog(true);
      return;
    }
    
    console.log('Proceeding with status change (skipConfirmation or not payment step)');
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      
      // Check if moving TO a payment step
      const workflow = getWorkflowForOrderType(order.orderTypeLabel);
      const targetStep = workflow?.find(step => step.label === newStatus);
      const isMovingToPaymentStep = targetStep?.isPaymentStep;
      
      // If moving TO a payment step, set paymentStatus to pending_payment
      if (isMovingToPaymentStep && order.paymentStatus !== 'completed') {
        console.log('Setting paymentStatus to pending_payment');
        await axios.patch(
          `${backendUrl}/api/orders/${orderId}`,
          { paymentStatus: 'pending_payment' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // If moving to "Completed", also update payment status
      if (newStatus.toLowerCase() === 'completed') {
        console.log('Updating payment status to completed');
        
        // Determine payment mode based on whether this was manual confirmation
        const paymentMode = skipConfirmation ? 'Counter' : 'Pay at Table';
        
        // Update payment status to completed and set payment mode
        await axios.patch(
          `${backendUrl}/api/orders/${orderId}`,
          { 
            paymentStatus: 'completed',
            paymentMode: paymentMode
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Update order status
      console.log('Updating order status to:', newStatus);
      console.log('Backend URL:', backendUrl);
      console.log('Order ID:', orderId);
      console.log('Full URL:', `${backendUrl}/api/orders/${orderId}/status`);
      console.log('Params:', { status: newStatus });
      
      const response = await axios.put(
        `${backendUrl}/api/orders/${orderId}/status`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { status: newStatus }
        }
      );

      console.log('Status update response:', response.data);
      if (response.data.success) {
        console.log('Status updated successfully, refreshing orders');
        // Refresh orders
        await fetchOrders();
      } else {
        console.error('Status update failed:', response.data);
        setErrorMessage('Failed to update order status: ' + (response.data.message || 'Unknown error'));
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error occurred';
      setErrorMessage('Error updating order status: ' + errorMsg);
      setShowErrorModal(true);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className={`min-h-screen ${isFullscreen ? 'bg-white' : 'bg-gray-50'}`}>
      <div className={`${isFullscreen ? 'w-full' : 'max-w-6xl mx-auto'} ${isFullscreen ? '' : 'px-4 sm:px-6 lg:px-8 py-8'}`}>
        
        <div className={`bg-white ${isFullscreen ? 'fixed inset-0 z-50 flex flex-col' : 'rounded-xl shadow-lg'} overflow-hidden`}>
          {/* Header - Compact Design like AI In Review */}
          <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-md">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {showCompleted ? 'Completed Orders' : 'Manage Orders'}
                  </h2>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  {/* Refresh Orders Button */}
                  <button
                    onClick={async () => {
                      await fetchOrders(true);
                    }}
                    disabled={isRefreshing}
                    className={`px-2 py-1.5 bg-gray-200 text-black text-sm font-medium rounded hover:bg-gray-300 transition-colors flex items-center justify-center ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Refresh orders from database"
                  >
                    <svg className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  
                  {/* PIN Display Badge - Only show on Dine In tab */}
                  {activeTab === 'inStore' && currentPIN && currentPIN.pin && (
                    <div className="bg-yellow-400 text-gray-900 text-sm px-3 py-1.5 rounded font-medium flex items-center gap-1.5">
                      <span className="font-semibold">PIN:</span>
                      <span className="font-bold tracking-wider">{currentPIN.pin}</span>
                      <button
                        onClick={regeneratePIN}
                        disabled={pinRegenerating}
                        className="p-0.5 hover:bg-yellow-500 rounded transition-colors disabled:opacity-50"
                        title="Regenerate PIN"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${pinRegenerating ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  )}
                  
                  {/* Barcode Management Button - Only show on Dine In tab */}
                  {activeTab === 'inStore' && ecommerceSettings?.payAtTable?.enableBarcodeScanning && (
                    <button
                      onClick={openBarcodeModal}
                      className="bg-purple-600 hover:bg-purple-700 text-white text-sm px-3 py-1.5 rounded font-medium flex items-center gap-1.5 transition-colors"
                      title="Manage Barcodes"
                    >
                      <QrCode className="h-4 w-4" />
                      <span>Barcodes</span>
                    </button>
                  )}
                  
                  {/* Date Display Badge */}
                  <div className="bg-orange-500 text-white text-sm px-3 py-1.5 rounded font-medium">
                    {getFormattedDateDisplay()}
                  </div>
                  
                  {/* Time Filter Dropdown */}
                  <select
                    value={timeFilter}
                    onChange={(e) => setTimeFilter(e.target.value)}
                    className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-colors appearance-none pr-8"
                    style={{ 
                      backgroundColor: 'var(--theme-primary)',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 6px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '14px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--theme-primary-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'var(--theme-primary)';
                    }}
                  >
                    <option value="yesterday" style={{ backgroundColor: 'white', color: 'black' }}>Yesterday</option>
                    <option value="today" style={{ backgroundColor: 'white', color: 'black' }}>Today</option>
                    <option value="tomorrow" style={{ backgroundColor: 'white', color: 'black' }}>Tomorrow</option>
                    <option value="past7days" style={{ backgroundColor: 'white', color: 'black' }}>Past 7 Days</option>
                    <option value="past30days" style={{ backgroundColor: 'white', color: 'black' }}>Past 30 Days</option>
                    <option value="past180days" style={{ backgroundColor: 'white', color: 'black' }}>Past 180 Days</option>
                  </select>
                  
                  {/* Completed Button */}
                  <button
                    onClick={() => setShowCompleted(!showCompleted)}
                    className={`text-sm px-4 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors ${
                      showCompleted
                        ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-400'
                    }`}
                  >
                    {showCompleted ? '✓ Completed' : 'Completed'}
                  </button>
                  
                  {/* View Mode Toggle */}
                  <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                    <button
                      onClick={() => setViewMode('list')}
                      className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                        viewMode === 'list'
                          ? 'bg-[rgb(41,173,211)] text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                      title="List View"
                    >
                      <List className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('card')}
                      className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-300 ${
                        viewMode === 'card'
                          ? 'bg-[rgb(41,173,211)] text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                      title="Card View"
                    >
                      <Grid className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Fullscreen Toggle Button */}
                  <button
                    onClick={() => setIsFullscreen(!isFullscreen)}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                    title={isFullscreen ? "Exit Fullscreen" : "Fullscreen View"}
                  >
                    {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs - Styled like business-info page */}
        {!isFullscreen && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('pickup')}
              className={`flex-1 py-3 px-4 text-center text-sm font-medium transition-colors ${
                activeTab === 'pickup'
                  ? 'text-[rgb(41,173,211)] border-b-2 border-[rgb(41,173,211)]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Pickup ({tabCounts.pickupCount})
            </button>
            <button
              onClick={() => setActiveTab('delivery')}
              className={`flex-1 py-3 px-4 text-center text-sm font-medium transition-colors ${
                activeTab === 'delivery'
                  ? 'text-[rgb(41,173,211)] border-b-2 border-[rgb(41,173,211)]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Delivery ({tabCounts.deliveryCount})
            </button>
            <button
              onClick={() => setActiveTab('shipping')}
              className={`flex-1 py-3 px-4 text-center text-sm font-medium transition-colors ${
                activeTab === 'shipping'
                  ? 'text-[rgb(41,173,211)] border-b-2 border-[rgb(41,173,211)]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Shipping ({tabCounts.shippingCount})
            </button>
            <button
              onClick={() => setActiveTab('inStore')}
              className={`flex-1 py-3 px-4 text-center text-sm font-medium transition-colors ${
                activeTab === 'inStore'
                  ? 'text-[rgb(41,173,211)] border-b-2 border-[rgb(41,173,211)]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Dine In ({tabCounts.inStoreCount})
            </button>
          </div>
        </div>
        )}

        {/* Orders List */}
        <div className={isFullscreen ? 'flex-1 overflow-y-auto' : 'overflow-y-auto'}>
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <p className="text-gray-600">Loading orders...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">
              {activeTab === 'pickup' && "No pickup orders for today"}
              {activeTab === 'delivery' && "No delivery orders for today"}
              {activeTab === 'shipping' && "No pending shipping orders"}
              {activeTab === 'inStore' && "No dine-in orders for today"}
            </p>
          </div>
        ) : viewMode === 'card' ? (
          /* Card View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {filteredOrders.map(order => {
              const { currentStep, nextStep } = getOrderSteps(order);
              const itemCount = order.items?.length || 0;
              const isInStore = order.orderType === 'orderAtTable' || order.orderTypeLabel === 'Order at Table' || 
                                (order.orderType === 'orderAtCounter' && order.specialInstructions === 'Dine In Order');
              
              return (
                <div
                  key={order.id}
                  className="bg-white rounded-lg border border-gray-200 hover:border-[rgb(41,173,211)] hover:shadow-lg transition-all flex flex-col"
                >
                  {isInStore ? (
                    /* In Store Card - Kitchen Chit Style */
                    <>
                      {/* Kitchen Chit Header */}
                      <div className="bg-gray-100 border-b border-gray-300 p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-bold text-gray-900">
                              {(order.customer?.name || 'Guest').split(' ')[0]}
                            </h3>
                            {currentStep?.isPaymentStep && order.paymentStatus === 'pending_payment' && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded flex items-center gap-1">
                                <CreditCard className="h-3 w-3" />
                                Payment Enabled
                              </span>
                            )}
                          </div>
                          {(order.table_name || order.assigned_table_name) && (
                            <div className="font-semibold text-sm" style={{ color: 'var(--theme-primary)' }}>
                              {order.table_name || order.assigned_table_name}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Card Body - Clickable */}
                      <div 
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowOrderModal(true);
                        }}
                        className="p-4 cursor-pointer flex-1"
                      >
                        {/* Items List - All items with options/variants */}
                        <div className="space-y-2.5">
                          {order.items && order.items.map((item, idx) => {
                            // Handle variant display
                            let variantText = '';
                            if (item.variant) {
                              if (typeof item.variant === 'string') {
                                variantText = item.variant;
                              } else if (typeof item.variant === 'object') {
                                variantText = `${item.variant.name || ''}`;
                              }
                            }

                            // Handle selected options
                            let optionsArray = [];
                            if (item.selectedOptions && item.options) {
                              Object.entries(item.selectedOptions).forEach(([optionId, value]) => {
                                const optionDef = item.options.find(opt => opt.option_id === optionId);
                                if (!optionDef) return;
                                
                                if (optionDef.option_type === 'single') {
                                  if (value === true) {
                                    optionsArray.push(`${optionDef.option_name}`);
                                  }
                                } else if (optionDef.option_type === 'dropdown') {
                                  const choice = optionDef.choices?.find(ch => ch.choice_id === value);
                                  if (choice) {
                                    optionsArray.push(`${optionDef.option_name}: ${choice.choice_name}`);
                                  }
                                }
                              });
                            }

                            return (
                              <div key={idx} className="text-sm border-b border-gray-100 pb-2 last:border-0">
                                <div className="font-semibold text-gray-900">
                                  {item.quantity}x {item.name}
                                </div>
                                
                                {/* Show variant if available */}
                                {variantText && (
                                  <div className="text-xs text-gray-600 ml-4 mt-0.5">
                                    • {item.variation_type && `${item.variation_type.charAt(0).toUpperCase() + item.variation_type.slice(1)}: `}{variantText}
                                  </div>
                                )}
                                
                                {/* Show selected options if available */}
                                {optionsArray.length > 0 && (
                                  <div className="text-xs text-gray-600 ml-4 mt-0.5 space-y-0.5">
                                    {optionsArray.map((opt, optIdx) => (
                                      <div key={optIdx}>• {opt}</div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Card Footer - Workflow Steps for In Store */}
                      <div className="border-t border-gray-200 bg-gray-50 p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 px-3 py-2 text-sm font-medium bg-gray-200 text-gray-700 rounded-lg cursor-default"
                          >
                            {currentStep?.label || order.status}
                          </button>
                          {nextStep && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusChange(order.id, nextStep.label);
                              }}
                              className="flex-1 px-3 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                              style={{ backgroundColor: 'var(--theme-primary)' }}
                            >
                              {nextStep.label}
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    /* Regular Order Card - Pickup/Delivery/Shipping */
                    <div 
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowOrderModal(true);
                      }}
                      className="p-4 cursor-pointer flex-1"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-gray-500">#{order.order_number || order.id?.substring(0, 8)}</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(currentStep?.label || order.status)} text-white`}>
                          {currentStep?.label || order.status}
                        </span>
                      </div>

                      {/* Customer Name */}
                      <h3 className="text-base font-semibold text-gray-900 mb-2">
                        {order.customer?.name || 'Guest'}
                      </h3>

                      <div className="space-y-2 text-sm text-gray-600 mb-3">
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-gray-400" />
                            <span>{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-400" />
                            <span className="text-xs">{formatOrderHeaderDateTime(order.orderDate)}</span>
                          </div>
                          {order.customer?.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-gray-400" />
                              <span className="text-xs">{order.customer.phone}</span>
                            </div>
                          )}
                        </div>

                      {/* Total */}
                      <div className="border-t border-gray-200 pt-3 mt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Total</span>
                          <span className="text-lg font-bold text-gray-900">${order.total?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* List View */
          <div className="space-y-4">
            {filteredOrders.map(order => {
              const isExpanded = expandedOrderId === order.id;
              const { currentStep, nextStep } = getOrderSteps(order);
              
              return (
                <div key={order.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* Order Header */}
                  <div
                    onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                    className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-gray-900">{order.customer?.name || 'N/A'}</span>
                        </div>
                        {order.orderTypeLabel === 'Shipping' && order.shippingOption ? (
                          <p className="text-xs text-gray-500">
                            {order.shippingOption.name}
                            {calculateEstimatedDelivery(order) && (
                              <span className="ml-2">• Est. Delivery: {calculateEstimatedDelivery(order)}</span>
                            )}
                          </p>
                        ) : (
                          <p className="text-xs text-gray-500">
                            {order.orderTypeLabel}: {formatOrderHeaderDateTime(order.orderDate)}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Current Workflow Step Badge */}
                        {currentStep ? (
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${getStatusColor(order.status)}`}>
                              {currentStep.label}
                            </span>
                            {/* Next Step Button */}
                            {nextStep && !isExpanded && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveToNextStep(order);
                                }}
                                className="px-2 py-1 bg-[rgb(41,173,211)] text-white text-xs font-medium rounded hover:bg-[rgb(31,153,191)] flex items-center gap-1"
                                title={`Move to: ${nextStep.label}`}
                              >
                                → {nextStep.label}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${getStatusColor(order.status)}`}>
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </span>
                        )}
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900">${order.total.toFixed(2)}</p>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Order Details */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        {/* Customer Details */}
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <h4 className="font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-100">Customer Details</h4>
                          <div className="space-y-2.5">
                            <div className="flex items-center gap-2 text-sm">
                              <User className="h-4 w-4 text-theme-blue" />
                              <span className="text-gray-700 font-medium">{order.customer?.name || 'N/A'}</span>
                            </div>
                            {order.customer?.phone && (
                              <div className="flex items-center gap-2 text-sm">
                                <Phone className="h-4 w-4 text-theme-blue" />
                                <span className="text-gray-700">{order.customer.phone}</span>
                              </div>
                            )}
                            {order.customer?.email && (
                              <div className="flex items-center gap-2 text-sm">
                                <Mail className="h-4 w-4 text-theme-blue" />
                                <span className="text-gray-700">{order.customer.email}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Order Details */}
                        <div className="bg-white p-4 rounded-lg border border-gray-200 md:col-span-2">
                          <h4 className="font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-100">Order Details</h4>
                          <div className="space-y-2.5">
                            {/* Order Number */}
                            <div className="flex items-center gap-2 text-sm">
                              <Package className="h-4 w-4 text-gray-500" />
                              <span className="text-gray-700">
                                <span className="font-medium">Order Number:</span> {order.order_number}
                              </span>
                            </div>

                            {/* Order Type */}
                            <div className="flex items-center gap-2 text-sm">
                              <Package className="h-4 w-4 text-green-500" />
                              <span className="text-gray-700">
                                <span className="font-medium">Order Type:</span> {order.orderTypeLabel || 
                                 (order.orderType === 'orderAtCounter' ? 'Order at Counter' : 
                                  order.orderType === 'orderAtTable' ? 'Order at Table' : 'N/A')}
                              </span>
                            </div>

                            {/* Order Date */}
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="h-4 w-4 text-theme-blue" />
                              <span className="text-gray-700">
                                <span className="font-medium">Order Date:</span> {formatOrderHeaderDateTime(order.orderDate)}
                              </span>
                            </div>

                            {/* Pickup/Delivery Time */}
                            {(order.pickupDate || order.deliveryDate || order.pickupTime || order.deliveryTime) && (
                              <div className="flex items-center gap-2 text-sm">
                                <Clock className="h-4 w-4 text-purple-500" />
                                <span className="text-gray-700">
                                  <span className="font-medium">
                                    {order.orderTypeLabel?.includes('Pickup') ? 'Pickup Time:' : 
                                     order.orderTypeLabel?.includes('Delivery') ? 'Delivery Time:' : 
                                     'Scheduled Time:'}
                                  </span> {
                                    order.pickupDate || order.deliveryDate ? (
                                      formatOrderHeaderDateTime(order.pickupDate || order.deliveryDate) + (order.pickupTime || order.deliveryTime ? `, ${order.pickupTime || order.deliveryTime}` : '')
                                    ) : (order.pickupTime || order.deliveryTime) ? (
                                      `${formatOrderHeaderDateTime(order.orderDate).split(',')[0]}, ${order.pickupTime || order.deliveryTime} EST`
                                    ) : 'N/A'
                                  }
                                </span>
                              </div>
                            )}

                            {/* Pickup Location */}
                            {order.pickupLocation && (
                              <div className="flex items-start gap-2 text-sm">
                                <MapPin className="h-4 w-4 text-orange-500 mt-0.5" />
                                <span className="text-gray-700">
                                  <span className="font-medium">Pickup Location:</span> {order.pickupLocation}
                                </span>
                              </div>
                            )}

                            {/* Delivery Address */}
                            {order.deliveryAddress && !order.pickupLocation && (
                              <div className="flex items-start gap-2 text-sm">
                                <MapPin className="h-4 w-4 text-orange-500 mt-0.5" />
                                <span className="text-gray-700">
                                  <span className="font-medium">Delivery Address:</span> {order.deliveryAddress}
                                </span>
                              </div>
                            )}

                            {/* Shipping Method */}
                            {order.orderTypeLabel === 'Shipping' && order.shippingOption && (
                              <div className="flex items-center gap-2 text-sm">
                                <Truck className="h-4 w-4 text-purple-500" />
                                <span className="text-gray-700">
                                  <span className="font-medium">Shipping Method:</span> {order.shippingOption.name}
                                  {order.shippingOption.description && (
                                    <span className="ml-2 text-xs text-gray-500">({order.shippingOption.description})</span>
                                  )}
                                </span>
                              </div>
                            )}

                            {/* Estimated Delivery Date for Shipping Orders */}
                            {order.orderTypeLabel === 'Shipping' && calculateEstimatedDelivery(order) && (
                              <div className="flex items-center gap-2 text-sm">
                                <Clock className="h-4 w-4 text-green-500" />
                                <span className="text-gray-700">
                                  <span className="font-medium">Est. Delivery Date:</span> {calculateEstimatedDelivery(order)}
                                </span>
                              </div>
                            )}

                            {/* Tracking Link */}
                            {order.tracking_link && (
                              <div className="flex items-center gap-2 text-sm">
                                <Truck className="h-4 w-4 text-[rgb(41,173,211)]" />
                                <span className="text-gray-700">
                                  <span className="font-medium">Tracking URL:</span>{' '}
                                  <a
                                    href={order.tracking_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[rgb(41,173,211)] hover:text-[rgb(31,153,191)] underline inline-flex items-center gap-1"
                                  >
                                    Track Shipment
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Order Items - Full Width */}
                      <div className="mb-4 bg-white p-4 rounded-lg border border-gray-200">
                        <h4 className="font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-100">Order Items</h4>
                        <div className="space-y-2">
                          {order.items && order.items.map((item, idx) => {
                            // Handle variant display
                            let variantText = '';
                            if (item.variant) {
                              if (typeof item.variant === 'string') {
                                variantText = item.variant;
                              } else if (typeof item.variant === 'object') {
                                variantText = `${item.variant.name || ''}`;
                              }
                            }

                            // Handle selected options
                            let optionsArray = [];
                            if (item.selectedOptions && item.options) {
                              // selectedOptions is an object with option_id as keys
                              // options is an array with full option details
                              Object.entries(item.selectedOptions).forEach(([optionId, value]) => {
                                // Find the option definition
                                const optionDef = item.options.find(opt => opt.option_id === optionId);
                                if (!optionDef) return;
                                
                                if (optionDef.option_type === 'single') {
                                  // Single checkbox option
                                  if (value === true) {
                                    const priceText = optionDef.price ? ` (+$${optionDef.price})` : '';
                                    optionsArray.push(`${optionDef.option_name}${priceText}`);
                                  }
                                } else if (optionDef.option_type === 'dropdown') {
                                  // Dropdown option - value is the choice_id
                                  const choice = optionDef.choices?.find(ch => ch.choice_id === value);
                                  if (choice) {
                                    const priceText = choice.price ? ` (+$${choice.price})` : '';
                                    optionsArray.push(`${optionDef.option_name}: ${choice.choice_name}${priceText}`);
                                  }
                                }
                              });
                            }

                            return (
                              <div key={idx} className="bg-gray-50 p-2.5 rounded border border-gray-200">
                                <div className="flex justify-between items-start text-sm mb-1">
                                  <div className="flex-1 pr-4">
                                    <span className="text-gray-900 font-medium break-words">
                                      {item.quantity}x {item.name || 'Unknown Item'}
                                    </span>
                                    
                                    {/* Show variant if available */}
                                    {variantText && (
                                      <div className="text-xs text-theme-blue ml-4 mt-1 break-words">
                                        • {item.variation_type && `${item.variation_type.charAt(0).toUpperCase() + item.variation_type.slice(1)}: `}{variantText}
                                        {item.variant?.price && ` ($${parseFloat(item.variant.price).toFixed(2)})`}
                                      </div>
                                    )}
                                    
                                    {/* Show selected options if available */}
                                    {optionsArray.length > 0 && (
                                      <div className="text-xs text-gray-600 ml-4 mt-1 space-y-0.5">
                                        {optionsArray.map((opt, optIdx) => (
                                          <div key={optIdx} className="break-words">• {opt}</div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <span className="text-gray-900 font-semibold whitespace-nowrap">
                                    ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Order Totals */}
                        <div className="border-t border-gray-200 mt-4 pt-4 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Subtotal</span>
                            <span className="text-gray-900">${order.subtotal.toFixed(2)}</span>
                          </div>
                          {order.tax > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Tax</span>
                              <span className="text-gray-900">${order.tax.toFixed(2)}</span>
                            </div>
                          )}
                          {order.shippingFee > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Shipping</span>
                              <span className="text-gray-900">${order.shippingFee.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200">
                            <span className="text-gray-900">Total</span>
                            <span className="text-gray-900">${order.total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="border-t border-gray-200 pt-4">
                        <div className="flex flex-wrap gap-2">
                            {/* Share Order Button */}
                            <button
                              onClick={() => alert('Share functionality - Generate link for: ' + order.order_number)}
                              className="px-3 py-1.5 bg-theme-blue text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1"
                            >
                              <ExternalLink className="h-4 w-4" />
                              Share Order
                            </button>

                            {/* Track Order Button (Only for Shipping) */}
                            {order.orderTypeLabel === 'Shipping' && (
                              <button
                                onClick={() => alert('Track order functionality for: ' + order.order_number)}
                                className="px-3 py-1.5 bg-[rgb(41,173,211)] text-white text-sm rounded hover:bg-[rgb(31,153,191)] flex items-center gap-1"
                              >
                                <Truck className="h-4 w-4" />
                                Track Order
                              </button>
                            )}

                            {/* Delete Order Button */}
                            <button
                              onClick={() => {
                                if (window.confirm(`Are you sure you want to delete order ${order.order_number}?`)) {
                                  alert('Delete functionality for: ' + order.order_number);
                                }
                              }}
                              className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center gap-1"
                            >
                              <Package className="h-4 w-4" />
                              Delete Order
                            </button>
                          </div>
                        </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </div>

        {/* Order Details Modal */}
        {showOrderModal && selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
              {/* Modal Header */}
              <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-primary)' }}>
                <h3 className="text-base font-semibold text-white">Order Details</h3>
                <div className="flex items-center gap-3">
                  {(selectedOrder.table_name || selectedOrder.assigned_table_name) && (
                    <div className="px-3 py-1 rounded-lg bg-white text-[rgb(41,173,211)] font-semibold text-sm">
                      {selectedOrder.table_name || selectedOrder.assigned_table_name}
                    </div>
                  )}
                  <button
                    onClick={() => setShowOrderModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                    aria-label="Close modal"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto flex-1 space-y-4">
                {/* Order Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Order Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Order Number:</span>
                      <span className="font-medium text-gray-900">#{selectedOrder.order_number || selectedOrder.id?.substring(0, 8)}</span>
                    </div>
                    {(selectedOrder.table_name || selectedOrder.assigned_table_name) && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Table:</span>
                        <span className="font-medium text-gray-900">{selectedOrder.table_name || selectedOrder.assigned_table_name}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Order Type:</span>
                      <span className="font-medium text-gray-900">
                        {selectedOrder.orderTypeLabel || 
                         (selectedOrder.orderType === 'orderAtCounter' ? 'Order at Counter' : 
                          selectedOrder.orderType === 'orderAtTable' ? 'Order at Table' : 'N/A')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Order Time:</span>
                      <span className="font-medium text-gray-900">{formatOrderHeaderDateTime(selectedOrder.orderDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Order Status:</span>
                      <span className="font-medium text-gray-900">
                        {(() => {
                          const { currentStep } = getOrderSteps(selectedOrder);
                          return currentStep?.label || selectedOrder.status || 'N/A';
                        })()}
                      </span>
                    </div>
                    {selectedOrder.paymentStatus && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment Status:</span>
                        <span className="font-medium text-gray-900">
                          {selectedOrder.paymentStatus === 'pending_payment' ? 'Pending' :
                           selectedOrder.paymentStatus === 'completed' ? 'Completed' :
                           selectedOrder.paymentStatus === 'pending' ? 'Pending' :
                           selectedOrder.paymentStatus.charAt(0).toUpperCase() + selectedOrder.paymentStatus.slice(1).replace(/_/g, ' ')}
                        </span>
                      </div>
                    )}
                    {selectedOrder.paymentMode && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment Mode:</span>
                        <span className="font-medium text-green-700 flex items-center gap-1">
                          {selectedOrder.paymentMode === 'Pay at Table' ? (
                            <>
                              <CreditCard className="h-4 w-4" />
                              Pay at Table
                            </>
                          ) : selectedOrder.paymentMode === 'Online' ? (
                            <>
                              <CreditCard className="h-4 w-4" />
                              Online
                            </>
                          ) : selectedOrder.paymentMode === 'Counter' ? (
                            <>
                              <Package className="h-4 w-4" />
                              Counter
                            </>
                          ) : (
                            <>
                              {selectedOrder.paymentMode}
                            </>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Customer Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Customer Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-medium text-gray-900">{selectedOrder.customer?.name || 'N/A'}</span>
                    </div>
                    {selectedOrder.customer?.email && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-medium text-gray-900">{selectedOrder.customer.email}</span>
                      </div>
                    )}
                    {selectedOrder.customer?.phone && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium text-gray-900">{selectedOrder.customer.phone}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Items */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Order Items</h4>
                  <div className="space-y-3">
                    {selectedOrder.items && selectedOrder.items.map((item, idx) => {
                      // Handle variant display
                      let variantText = '';
                      if (item.variant) {
                        if (typeof item.variant === 'string') {
                          variantText = item.variant;
                        } else if (typeof item.variant === 'object') {
                          variantText = `${item.variant.name || ''}`;
                        }
                      }

                      // Handle selected options
                      let optionsArray = [];
                      if (item.selectedOptions) {
                        if (item.options && Array.isArray(item.options)) {
                          // Full option definitions are available
                          Object.entries(item.selectedOptions).forEach(([optionId, value]) => {
                            const optionDef = item.options.find(opt => opt.option_id === optionId);
                            if (!optionDef) return;
                            
                            if (optionDef.option_type === 'single') {
                              if (value === true) {
                                const priceText = optionDef.price ? ` (+$${optionDef.price})` : '';
                                optionsArray.push(`${optionDef.option_name}${priceText}`);
                              }
                            } else if (optionDef.option_type === 'dropdown') {
                              const choice = optionDef.choices?.find(ch => ch.choice_id === value);
                              if (choice) {
                                const priceText = choice.price ? ` (+$${choice.price})` : '';
                                optionsArray.push(`${optionDef.option_name}: ${choice.choice_name}${priceText}`);
                              }
                            }
                          });
                        } else {
                          // Only selectedOptions IDs available - show simple list
                          Object.entries(item.selectedOptions).forEach(([optionId, value]) => {
                            if (value === true) {
                              optionsArray.push(`Option: ${optionId}`);
                            } else if (typeof value === 'string') {
                              optionsArray.push(`Option: ${optionId} = ${value}`);
                            }
                          });
                        }
                      }

                      return (
                        <div key={idx} className="bg-white p-3 rounded border border-gray-200">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-900 font-medium">
                              {item.quantity}x {item.name || 'Unknown Item'}
                            </span>
                            <span className="text-gray-900 font-semibold">
                              ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                            </span>
                          </div>
                          
                          {/* Show variant if available */}
                          {variantText && (
                            <div className="text-xs text-theme-blue ml-4 mt-1">
                              • {item.variation_type && `${item.variation_type.charAt(0).toUpperCase() + item.variation_type.slice(1)}: `}{variantText}
                              {item.variant?.price && ` ($${parseFloat(item.variant.price).toFixed(2)})`}
                            </div>
                          )}
                          
                          {/* Show selected options if available */}
                          {optionsArray.length > 0 && (
                            <div className="text-xs text-gray-600 ml-4 mt-1 space-y-0.5">
                              {optionsArray.map((opt, optIdx) => (
                                <div key={optIdx}>• {opt}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Order Total */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Order Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="text-gray-900">${selectedOrder.subtotal?.toFixed(2) || '0.00'}</span>
                    </div>
                    {selectedOrder.tax > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax</span>
                        <span className="text-gray-900">${selectedOrder.tax.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedOrder.tip > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tip</span>
                        <span className="text-green-600">${selectedOrder.tip.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedOrder.shippingFee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Shipping</span>
                        <span className="text-gray-900">${selectedOrder.shippingFee.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t border-gray-300 pt-2 mt-2">
                      <div className="flex justify-between font-bold text-gray-900">
                        <span>Total</span>
                        <span>${selectedOrder.total?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => {
                    setShowOrderModal(false);
                    setSelectedOrder(null);
                  }}
                  className="w-full px-4 py-2 bg-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Processing Modal */}
        {isProcessing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-xs w-full mx-4">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(41,173,211)]"></div>
                <p className="text-gray-700 font-medium">Processing...</p>
              </div>
            </div>
          </div>
        )}

        {/* Payment Enabled Success Modal */}
        {showPaymentEnabledModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-green-600" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Enabled!</h3>
                  <p className="text-sm text-gray-600">
                    Customer can now select <span className="font-semibold text-[rgb(41,173,211)]">{enabledTableName}</span> in Pay at Table to complete payment.
                  </p>
                </div>
                <button
                  onClick={() => setShowPaymentEnabledModal(false)}
                  className="w-full px-4 py-2 bg-[rgb(41,173,211)] text-white rounded-lg hover:bg-[rgb(31,163,201)] font-medium transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Barcode Management Modal */}
        {showBarcodeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-5 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Barcode Management</h3>
                <button
                  onClick={() => setShowBarcodeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="p-5 overflow-y-auto max-h-[calc(80vh-80px)]">
                {loadingBarcodes ? (
                  <div className="text-center py-8 text-gray-600">Loading barcodes...</div>
                ) : barcodes.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">
                    No barcodes generated yet. Go to Settings &gt; E-Commerce Settings &gt; Pay at Table to generate barcodes.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {barcodes.map((barcode) => (
                      <div key={barcode.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-start gap-4">
                          <img 
                            src={`data:image/png;base64,${barcode.qr_code_image}`}
                            alt={`Barcode ${barcode.barcode_number}`}
                            className="w-20 h-20 border border-gray-300 rounded flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-base font-semibold text-gray-900">Barcode #{barcode.barcode_number}</h4>
                              {barcode.assigned_table_name && (
                                <span className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded font-medium">
                                  Assigned
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-1 break-all">{barcode.url}</p>
                            {barcode.assigned_table_name && (
                              <div className="flex items-center justify-between mt-2">
                                <p className="text-sm text-theme-blue font-medium">
                                  Assigned to: {barcode.assigned_table_name}
                                </p>
                                <button
                                  onClick={() => handleUnassignBarcode(barcode.id)}
                                  className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200 font-medium transition-colors"
                                >
                                  Unassign
                                </button>
                              </div>
                            )}
                            {!barcode.assigned_table_name && (
                              <div className="mt-3">
                                <label className="text-xs font-medium text-gray-700 block mb-1">Assign to order:</label>
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      handleAssignBarcode(barcode.id, e.target.value);
                                      e.target.value = ''; // Reset selection
                                    }
                                  }}
                                  className="text-sm border border-gray-300 rounded px-3 py-1.5 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                  <option value="">Select a table...</option>
                                  {(() => {
                                    // Group payment-enabled orders by table
                                    const paymentOrders = orders.filter(order => order.paymentStatus === 'pending_payment');
                                    const tableGroups = {};
                                    
                                    paymentOrders.forEach(order => {
                                      const tableName = order.table_name || order.assigned_table_name || 'No Table';
                                      const tableId = order.assigned_table_id || order.id;
                                      
                                      if (!tableGroups[tableName]) {
                                        tableGroups[tableName] = {
                                          tableId: tableId,
                                          tableName: tableName,
                                          orders: []
                                        };
                                      }
                                      tableGroups[tableName].orders.push(order);
                                    });
                                    
                                    return Object.values(tableGroups).map(group => {
                                      // Use the first order's ID to represent the table
                                      const representativeOrderId = group.orders[0].id;
                                      const orderCount = group.orders.length;
                                      const displayText = orderCount > 1 
                                        ? `${group.tableName} (${orderCount} Orders)`
                                        : `${group.tableName}`;
                                      
                                      return (
                                        <option key={group.tableName} value={representativeOrderId}>
                                          {displayText}
                                        </option>
                                      );
                                    });
                                  })()}
                                </select>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payment Confirmation Dialog */}
        {showPaymentConfirmDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Payment Confirmation</h3>
                    <p className="text-sm text-gray-600">Manual order completion</p>
                  </div>
                </div>
                
                <div className="mb-6">
                  <p className="text-sm text-gray-700">
                    This order is currently in a payment step. Can you confirm that payment has been received for this order?
                  </p>
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800">
                      <strong>Note:</strong> Confirming will mark this order as completed and update the payment status.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowPaymentConfirmDialog(false);
                      setPendingStatusChange(null);
                    }}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      console.log('Confirmation button clicked, pendingStatusChange:', pendingStatusChange);
                      setShowPaymentConfirmDialog(false);
                      setPendingStatusChange(null);
                      
                      if (pendingStatusChange) {
                        await handleStatusChange(pendingStatusChange.orderId, pendingStatusChange.newStatus, true);
                      }
                    }}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    Yes, Payment Received
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Modal */}
        {showErrorModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Error</h3>
                    <p className="text-sm text-gray-600">Something went wrong</p>
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{errorMessage}</p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setShowErrorModal(false);
                      setErrorMessage('');
                    }}
                    className="px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default CurrentOrders;
